import { forwardRef, HtmlHTMLAttributes, memo, ReactNode } from "react";
import classNames from "classnames";
import { RefType } from "../../types";
import { Badge, BadgeProps } from "../Badge";
import { mapBadgeGroupColorToBadgeColor, mapBadgeGroupSizeToBadgeGroup, mapBadgeGroupTypeToBadgeType } from "./utils";

import styles from "./styles.module.scss";

export type BadgeGroupSize = "md" | "lg";
export type BadgeGroupColor = "gray" | "brand" | "error" | "warning" | "success";
export type BadgeGroupType = "light" | "outline" | "modern";

export type BadgeGroupProps = HtmlHTMLAttributes<HTMLDivElement> & {
  ref?: RefType<HTMLDivElement>
  badgeGroupSize?: BadgeGroupSize,
  badgeGroupColor?: BadgeGroupColor;
  badgeGroupType?: BadgeGroupType;
  badgeProps: BadgeProps
} & (
  {
    icon?: never,
    iconPosition?: never,
    iconBlockProps?: never
    badgePosition?: "start" | "end"
  } |
  ({
    icon: ReactNode,
    iconBlockProps?: HtmlHTMLAttributes<HTMLDivElement>
  } & (
    {
      iconPosition?: "end",
      badgePosition?: "start"
    } |
    {
      iconPosition?: "start",
      badgePosition?: "end"
    })))

export const BadgeGroup = memo<BadgeGroupProps>(forwardRef((
  {
    badgeProps,
    badgeGroupSize = "lg",
    badgeGroupColor = "brand",
    badgeGroupType = "light",
    badgePosition,
    icon,
    iconPosition,
    iconBlockProps,
    className,
    children,
    ...props
  },
  ref,
) => {
  const finalBadgePosition = badgePosition || (iconPosition === "start" ? "end" : "start");

  const iconBlock = (!!icon) && (
    <div {...iconBlockProps} className={classNames(iconBlockProps?.className, styles.icon)}>
      {icon}
    </div>
  );

  const badgeBlock = (
    <Badge
      {...badgeProps}
      badgeSize={mapBadgeGroupSizeToBadgeGroup(badgeGroupSize)}
      badgeColor={mapBadgeGroupColorToBadgeColor(badgeGroupColor)}
      badgeType={mapBadgeGroupTypeToBadgeType(badgeGroupType)}
      className={classNames(badgeProps.className, styles.badge)}
    />
  );

  return (
    <div
      ref={ref}
      className={classNames(
        className,
        styles.root,
        styles[badgeGroupSize as keyof typeof styles],
        styles[badgeGroupColor as keyof typeof styles],
        styles[badgeGroupType as keyof typeof styles],
        {
          [styles.hasStartContent] : finalBadgePosition === "start",
          [styles.hasEndContent] : finalBadgePosition === "end",
        }
      )}
      data-badge-group-size={badgeGroupSize}
      data-badge-group-color={badgeGroupColor}
      data-badge-group-type={badgeGroupType}
      {...props}
    >
      {finalBadgePosition === "start" ? badgeBlock : iconBlock}
      {children}
      {finalBadgePosition === "end" ? badgeBlock : iconBlock}
    </div>
  );
}));